import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Orbit, PenLine, Plus, Sprout, Sunrise } from "lucide-react";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useSpaces } from "../../../context/SpaceProvider";
import { listTraits, type UserTrait } from "../../../api/userTraits";
import {
  listPwfs,
  createPwf,
  updatePwf,
  getQuestSummary,
  type Pwf,
  type QuestSummary,
} from "../../../api/pwf";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { Textarea } from "../../ui/textarea";
import { scrollReveal } from "../motionConstants";
import AutoenginuityGaps, { isGapTrait } from "./AutoenginuityGaps";
import ClaryNudge from "./ClaryNudge";

const TRAIT_LEVELS = ["Aspiration", "Emerging", "Developing", "Embodied"];

// The compass node: a single root pwf holding the learner's horizon (stored as
// config.cockpit.futureStatement — internal field name kept for data compat).
// It lives IN the pwf world like everything else — no side-tables.
const compassOf = (pwfs: Pwf[]) =>
  pwfs.find((p) => !p.isTemplate && !p.pwfMeta?.fiParent && (p.config as any)?.cockpit) ?? null;

const Compass = ({ headerExtra }: { headerExtra?: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { spaces, hasSpaces } = useSpaces();

  const { data: pwfs, isLoading } = useQuery({
    queryKey: ["cockpit-compass-pwfs"],
    queryFn: () => listPwfs(retryFetch),
    staleTime: 30_000,
  });

  // Becoming progress straight from the quest machinery of journeys in motion
  const { data: becomings } = useQuery({
    queryKey: ["cockpit-becomings"],
    queryFn: async () => {
      const roots = (pwfs ?? [])
        .filter(
          (p) =>
            p.pwfType === "pwf" &&
            !p.isTemplate &&
            !p.pwfMeta?.fiParent &&
            !["completed", "archived", "paused"].includes(p.lifecycleState) &&
            !(p.config as any)?.cockpit
        )
        .slice(0, 4);
      const settled = await Promise.allSettled(
        roots.map((r) => getQuestSummary(retryFetch, r._id))
      );
      return settled
        .filter((s): s is PromiseFulfilledResult<QuestSummary> => s.status === "fulfilled")
        .map((s) => s.value)
        .filter((q) => q.identity || q.journey.become?.identity);
    },
    enabled: !!pwfs,
    staleTime: 60_000,
  });

  // Same query the gaps strip runs (shared key → shared cache) — the nudge
  // wants to know about gaps that aren't in any plan yet.
  const { data: traits } = useQuery({
    queryKey: ["cockpit-gap-traits"],
    queryFn: () => listTraits(retryFetch) as Promise<UserTrait[]>,
    staleTime: 30_000,
  });
  const unplannedGap = useMemo(
    () => (traits ?? []).filter(isGapTrait).find((g) => !(g.pwfIds?.length ?? 0)),
    [traits]
  );

  const compassNode = useMemo(() => compassOf(pwfs ?? []), [pwfs]);
  const statement = ((compassNode?.config as any)?.cockpit?.futureStatement as string) ?? "";

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const saveStatement = async () => {
    const text = draft.trim();
    setSaving(true);
    try {
      if (compassNode) {
        await updatePwf(retryFetch, compassNode._id, {
          config: { cockpit: { ...(compassNode.config as any)?.cockpit, futureStatement: text } },
        });
      } else {
        await createPwf(retryFetch, {
          title: "My Compass",
          pwfType: "pwf",
          lifecycleState: "active",
          config: { cockpit: { futureStatement: text } },
        });
      }
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["cockpit-compass-pwfs"] });
      toast.success(t("cockpit_statement_saved"));
    } catch (e: any) {
      toast.error(e.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setDraft(statement);
    setEditing(true);
  };

  return (
    <motion.section {...scrollReveal} className="space-y-5">
      {/* Header — page title left, mode switch right (design 5b) */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("cockpit_mode_compass")}
          </h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            {t("cockpit_compass_subtitle")}
          </p>
        </div>
        {headerExtra && (
          <div className="flex flex-wrap items-center gap-2 pb-1">{headerExtra}</div>
        )}
      </div>

      {/* Horizon hero — the direction statement everything else steers toward */}
      <div className="rounded-[18px] border border-primary/20 bg-gradient-to-br from-card from-55% to-primary/[0.04] px-6 py-5 md:px-[30px] md:py-6">
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-primary/80 flex items-center gap-1.5">
            <Sunrise className="w-3.5 h-3.5" />
            {t("cockpit_horizon_full")}
          </p>
          {!editing && (
            <button
              onClick={startEditing}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 py-1 text-[11.5px] text-muted-foreground hover:text-foreground hover:border-border transition-colors"
            >
              <PenLine className="w-3 h-3" />
              {statement ? t("edit") : t("cockpit_write_statement")}
            </button>
          )}
        </div>
        {isLoading ? (
          <Skeleton className="h-16 w-full rounded-xl bg-gray-100" />
        ) : editing ? (
          <div>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t("cockpit_statement_placeholder")}
              rows={5}
              autoFocus
              className="rounded-xl text-sm leading-relaxed"
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setEditing(false)}>
                {t("cancel")}
              </Button>
              <Button size="sm" className="rounded-full" onClick={saveStatement} disabled={saving}>
                {saving && <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />}
                {t("save")}
              </Button>
            </div>
          </div>
        ) : statement ? (
          <p className="font-serif italic text-[17px] md:text-[19px] leading-[1.55] text-foreground/85 whitespace-pre-wrap max-w-[820px]">
            {statement}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground/80">{t("cockpit_statement_empty")}</p>
        )}
      </div>

      {/* Two columns: identity spaces + companion | who you're becoming + gaps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div className="min-w-0 space-y-4">
          {/* Identity spaces */}
          <div className="space-y-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
              <Orbit className="w-3 h-3 text-indigo-500" />
              {t("cockpit_spaces_title")}
            </p>
            {hasSpaces ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {spaces.map((s: any) => (
                  <button
                    key={s._id}
                    onClick={() => navigate(`/spaces?space=${s._id}`)}
                    className="rounded-2xl border border-border/70 bg-card px-4 py-3.5 text-start hover:shadow-md hover:border-indigo-200 transition-all"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2 min-w-0">
                      <span
                        className="w-[9px] h-[9px] rounded-full flex-none"
                        style={{ background: s.color || "#6366f1" }}
                      />
                      <span className="truncate">{s.name}</span>
                    </p>
                    <p className="text-base font-bold text-foreground mt-1.5 leading-snug line-clamp-2">
                      {s.identityLabel ?? s.name}
                    </p>
                    {s.vision && (
                      <p className="text-xs leading-normal text-muted-foreground mt-1 line-clamp-2">
                        {s.vision}
                      </p>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => navigate("/spaces")}
                  className="rounded-2xl border-[1.5px] border-dashed border-gray-200 px-4 py-3.5 flex items-center justify-center gap-1 text-xs text-muted-foreground/70 hover:text-indigo-600 hover:border-indigo-200 transition-colors min-h-[68px]"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t("cockpit_new_space")}
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate("/spaces")}
                className="w-full rounded-2xl border border-dashed border-indigo-200 p-6 text-center text-sm text-muted-foreground hover:text-indigo-600 hover:border-indigo-300 transition-colors"
              >
                {t("cockpit_spaces_empty")}
              </button>
            )}
          </div>

          {/* The companion reads the compass, not the user's mind — grounded nudges only */}
          {!statement && !isLoading ? (
            <ClaryNudge onClick={startEditing}>{t("cockpit_clary_no_horizon")}</ClaryNudge>
          ) : unplannedGap ? (
            <ClaryNudge>
              {t("cockpit_clary_gap_no_plan", { name: unplannedGap.name })}
            </ClaryNudge>
          ) : null}
        </div>

        <div className="min-w-0 space-y-5">
          {/* Who you're becoming — from the quest machinery */}
          {(becomings?.length ?? 0) > 0 && (
            <div className="space-y-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
                <Sprout className="w-3 h-3 text-emerald-600" />
                {t("cockpit_becoming_title")}
              </p>
              {becomings!.map((q) => {
                const identityName = q.identity?.identityLabel ?? q.journey.become?.identity;
                const traits = q.identity?.traits ?? [];
                return (
                  <button
                    key={q.journey.id}
                    onClick={() => navigate(`/pwf?map=${q.journey.id}`)}
                    className="w-full text-start rounded-2xl border border-emerald-300/50 bg-emerald-500/[0.04] px-[18px] py-[15px] hover:shadow-md transition-shadow"
                  >
                    <p className="text-[13.5px] text-emerald-900">
                      {t("cockpit_becoming")} <strong>{identityName}</strong>
                    </p>
                    <p className="text-[11.5px] text-emerald-800/60 truncate mt-0.5">
                      {t("cockpit_becoming_via", { title: q.journey.title })}
                    </p>
                    {traits.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {traits.slice(0, 5).map((tr) => (
                          <span
                            key={tr.id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-white/70 px-2.5 py-1 text-[11.5px] text-emerald-900"
                          >
                            {tr.emoji && <span>{tr.emoji}</span>}
                            {tr.name}
                            <span
                              className="flex items-center gap-[2px] ms-0.5"
                              title={TRAIT_LEVELS[tr.level]}
                            >
                              {[0, 1, 2, 3].map((lv) => (
                                <span
                                  key={lv}
                                  className={cn(
                                    "h-1 w-1 rounded-full",
                                    lv <= tr.level && tr.level > 0
                                      ? "bg-emerald-500"
                                      : "bg-emerald-200"
                                  )}
                                />
                              ))}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* ICDF §5.1 — the gaps being closed (Autoenginuity register) */}
          <AutoenginuityGaps />
        </div>
      </div>
    </motion.section>
  );
};

export default Compass;
